import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Select,
} from '@aeris/ui'

import {
  ApiError,
  createSalesChannel,
  fetchBranches,
  fetchPriceLists,
  updateSalesChannel,
  type SalesChannelSummaryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Create and edit a sales channel (F030).
 *
 * The three numbers on this form are the overselling policy, and the wording
 * has to carry that — a "buffer" field with no explanation is a field people
 * leave at zero, and zero means the marketplace may promise the last unit on
 * the shelf while somebody is holding it at the counter.
 *
 * The **kind and the code cannot be changed** once the channel exists. The code
 * lives on in reports and, one day, in imported orders; re-kinding a channel
 * would make everything it ever sold read as having come from somewhere else.
 */

const KIND_OPTIONS = [
  { value: 'mercado_livre', label: 'Mercado Livre' },
  { value: 'shopee', label: 'Shopee' },
  { value: 'ecommerce', label: 'E-commerce próprio' },
  { value: 'bling', label: 'Bling' },
  { value: 'other', label: 'Outro' },
]

const ROUNDING_OPTIONS = [
  { value: 'cent', label: 'Centavo (R$ 108,03)' },
  { value: 'tenth', label: 'Dez centavos (R$ 108,10)' },
  { value: 'unit', label: 'Real inteiro (R$ 109,00)' },
  { value: 'ninety', label: 'Terminado em ,90 (R$ 108,90)' },
]

const STATUS_OPTIONS = [
  { value: 'active', label: 'Ativo' },
  { value: 'paused', label: 'Pausado' },
  { value: 'archived', label: 'Arquivado' },
]

/** `3.000000` → `3`, for a field a person edits. */
const toField = (value: string | null): string => {
  if (value === null) return ''
  const [whole = '0', fraction = ''] = value.split('.')
  const trimmed = fraction.replace(/0+$/, '')
  return trimmed === '' ? whole : `${whole}.${trimmed}`
}

export type ChannelFormProps = {
  open: boolean
  /** Absent means "create". */
  channel?: SalesChannelSummaryType | undefined
  onOpenChange: (open: boolean) => void
  onSaved: () => Promise<void> | void
}

export const ChannelForm = ({
  open,
  channel,
  onOpenChange,
  onSaved,
}: ChannelFormProps): ReactNode => {
  const { session } = useAuth()
  const editing = channel !== undefined

  /*
   * Seeded once, from props.
   *
   * The caller mounts this only while the dialog is open and keys it on the
   * row being edited, so "reset the form" is a remount rather than an effect
   * writing state back into itself — which is the pattern the lint rule is
   * there to prevent and which also flashes the previous row's numbers for a
   * frame.
   */
  const [kind, setKind] = useState(channel?.kind ?? 'mercado_livre')
  const [code, setCode] = useState(channel?.code ?? '')
  const [name, setName] = useState(channel?.name ?? '')
  const [status, setStatus] = useState(channel?.status ?? 'active')
  const [branchIds, setBranchIds] = useState<string[]>([
    ...(channel?.branchIds ?? []),
  ])
  const [stockBuffer, setStockBuffer] = useState(
    toField(channel?.stockBuffer ?? '0')
  )
  const [maxPublished, setMaxPublished] = useState(
    toField(channel?.maxPublishedQuantity ?? null)
  )
  const [staleAfterHours, setStaleAfterHours] = useState(
    String(channel?.staleAfterHours ?? 6)
  )
  /* The three the product owner chose. All settings, none constants. */
  const [autoAccept, setAutoAccept] = useState(
    channel?.autoAcceptOrders ?? false
  )
  const [reserveAt, setReserveAt] = useState(channel?.reserveStockAt ?? 'order')
  const [fulfilment, setFulfilment] = useState(
    channel?.fulfilmentPolicy ?? 'nearest'
  )
  /* A política de preço (F110). Cada campo é uma decisão de quanto se ganha. */
  const [priceListId, setPriceListId] = useState(
    channel?.pricing.priceListId ?? ''
  )
  const [minMargin, setMinMargin] = useState(
    toField(channel?.pricing.minMarginPercent ?? '10')
  )
  const [passFixedFee, setPassFixedFee] = useState(
    channel?.pricing.passFixedFee ?? true
  )
  const [shippingShare, setShippingShare] = useState(
    toField(channel?.pricing.shippingSharePercent ?? '100')
  )
  const [rounding, setRounding] = useState(channel?.pricing.rounding ?? 'cent')
  const [warrantyMonths, setWarrantyMonths] = useState(
    String(channel?.pricing.defaultWarrantyMonths ?? 3)
  )
  const [allowWithoutCost, setAllowWithoutCost] = useState(
    channel?.pricing.allowWithoutCost ?? true
  )
  const [trustAiResearch, setTrustAiResearch] = useState(
    channel?.pricing.trustAiResearch ?? false
  )
  const [error, setError] = useState<string | null>(null)

  const priceLists = useQuery({
    queryKey: ['price-lists'],
    queryFn: async ({ signal }) => await fetchPriceLists(signal),
    enabled: open,
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: async ({ signal }) => await fetchBranches(undefined, signal),
    enabled: open,
  })

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        branchIds,
        stockBuffer: stockBuffer.trim() === '' ? '0' : stockBuffer.trim(),
        maxPublishedQuantity:
          maxPublished.trim() === '' ? null : maxPublished.trim(),
        staleAfterHours: Number(staleAfterHours),
        name: name.trim(),
        autoAcceptOrders: autoAccept,
        reserveStockAt: reserveAt,
        fulfilmentPolicy: fulfilment,
        pricing: {
          priceListId: priceListId === '' ? null : priceListId,
          minMarginPercent: minMargin.trim() === '' ? '0' : minMargin.trim(),
          passFixedFee,
          shippingSharePercent:
            shippingShare.trim() === '' ? '0' : shippingShare.trim(),
          rounding,
          defaultWarrantyMonths: Number(warrantyMonths),
          allowWithoutCost,
          trustAiResearch,
        },
      }

      if (editing) {
        await updateSalesChannel(channel.id, {
          ...payload,
          status,
          version: channel.version,
        })
        return
      }

      await createSalesChannel({
        ...payload,
        companyId: session?.context?.companyId ?? '',
        kind,
        code: code.trim().toUpperCase(),
      })
    },
    onSuccess: async () => {
      await onSaved()
      onOpenChange(false)
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar este canal.'
      )
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={editing ? 'Editar canal' : 'Novo canal'}
        description="Onde a loja vende além do balcão, e quanto do estoque esse lugar pode prometer."
        className="max-w-lg"
      >
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          {/*
            Fixed once created: everything this channel ever sold would
            otherwise start reading as having come from somewhere else.
          */}
          <Field label="Tipo" required>
            {(field) => (
              <Select
                {...field}
                value={kind}
                disabled={editing}
                onChange={(event) => {
                  setKind(event.target.value as typeof kind)
                }}
              >
                {KIND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Código"
            hint="Curto e permanente. Aparece em relatórios."
          >
            {(field) => (
              <Input
                {...field}
                value={code}
                disabled={editing}
                maxLength={30}
                onChange={(event) => {
                  setCode(event.target.value)
                }}
              />
            )}
          </Field>

          <Field label="Nome" required>
            {(field) => (
              <Input
                {...field}
                value={name}
                maxLength={120}
                onChange={(event) => {
                  setName(event.target.value)
                }}
              />
            )}
          </Field>

          {editing ? (
            <Field label="Situação">
              {(field) => (
                <Select
                  {...field}
                  value={status}
                  onChange={(event) => {
                    setStatus(event.target.value as typeof status)
                  }}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}

          <Field
            label="Filiais que abastecem este canal"
            hint="Nenhuma marcada significa que o canal não publica nada."
          >
            {() => (
              <div className="flex flex-col gap-1.5">
                {(branches.data?.branches ?? []).map((branch) => (
                  <label key={branch.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={branchIds.includes(branch.id)}
                      onChange={(event) => {
                        setBranchIds((current) =>
                          event.target.checked
                            ? [...current, branch.id]
                            : current.filter((value) => value !== branch.id)
                        )
                      }}
                    />
                    <span>{branch.name}</span>
                  </label>
                ))}
              </div>
            )}
          </Field>

          <Field
            label="Buffer de estoque"
            hint="Unidades reservadas ao balcão. Com 1, uma loja que tem 1 publica 0 — a última é de quem está com ela na mão."
          >
            {(field) => (
              <Input
                {...field}
                inputMode="decimal"
                value={stockBuffer}
                onChange={(event) => {
                  setStockBuffer(event.target.value)
                }}
              />
            )}
          </Field>

          <Field
            label="Teto por anúncio"
            hint="Em branco: sem teto. Serve para não revelar o tamanho do estoque."
          >
            {(field) => (
              <Input
                {...field}
                inputMode="decimal"
                value={maxPublished}
                onChange={(event) => {
                  setMaxPublished(event.target.value)
                }}
              />
            )}
          </Field>

          {/*
            The three answers, as controls.

            Auto-accept is a checkbox rather than a Select because it is a
            yes-or-no about this shop; the other two are genuine choices between
            two defensible behaviours, so they say what each one means rather
            than assuming somebody remembers.
          */}
          <label className="flex items-start gap-2 text-(length:--text-sm) text-(--text-secondary)">
            <input
              type="checkbox"
              className="mt-1"
              checked={autoAccept}
              onChange={(event) => {
                setAutoAccept(event.target.checked)
              }}
            />
            <span>
              Aceitar pedidos automaticamente
              <span className="block text-(length:--text-xs) text-(--text-muted)">
                Desmarcado, o pedido espera alguém aceitar. Comece assim até
                conferir que os anúncios estão ligados aos produtos certos.
              </span>
            </span>
          </label>

          <Field
            label="Reservar estoque"
            hint="No pedido protege quem já pagou; no despacho mantém a prateleira do balcão real até alguém tirar a peça dela."
          >
            {(field) => (
              <Select
                {...field}
                value={reserveAt}
                onChange={(event) => {
                  setReserveAt(event.target.value as typeof reserveAt)
                }}
              >
                <option value="order">No pedido</option>
                <option value="dispatch">No despacho</option>
              </Select>
            )}
          </Field>

          <Field
            label="Qual filial atende"
            hint="Com uma filial só, é ela. A regra já está pronta para escolher a mais próxima quando houver mais de uma."
          >
            {(field) => (
              <Select
                {...field}
                value={fulfilment}
                onChange={(event) => {
                  setFulfilment(event.target.value as typeof fulfilment)
                }}
              >
                <option value="nearest">A mais próxima</option>
                <option value="single">Sempre a primeira elegível</option>
              </Select>
            )}
          </Field>

          <Field
            label="Validade do saldo (horas)"
            hint="Filial sem notícias há mais que isso deixa de contar. Ela pode ter vendido a tarde inteira."
          >
            {(field) => (
              <Input
                {...field}
                inputMode="numeric"
                value={staleAfterHours}
                onChange={(event) => {
                  setStaleAfterHours(event.target.value)
                }}
              />
            )}
          </Field>

          {/*
            Preços no canal (F110).

            Uma seção e não campos soltos: as oito respostas juntas decidem UM
            número — o preço do anúncio —, e separá-las na tela faria cada uma
            parecer independente das outras. O texto de cada campo diz o que ela
            custa, porque nenhuma delas é óbvia pelo nome.
          */}
          <div className="mt-2 flex flex-col gap-4 border-t border-(--border-subtle) pt-4">
            <div>
              <h3 className="text-(length:--text-sm) font-medium text-(--text-primary)">
                Preços no canal
              </h3>
              <p className="text-(length:--text-xs) text-(--text-muted)">
                O preço do anúncio sai do preço de balcão com as taxas do canal
                já dentro. Abaixo da margem mínima o anúncio não é publicado, e
                a tela diz por quê.
              </p>
            </div>

            <Field
              label="Lista de preço"
              hint="De onde sai o preço de balcão que serve de base. Em branco: a lista padrão da conta."
            >
              {(field) => (
                <Select
                  {...field}
                  value={priceListId}
                  onChange={(event) => {
                    setPriceListId(event.target.value)
                  }}
                >
                  <option value="">Lista padrão</option>
                  {(priceLists.data?.priceLists ?? []).map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Margem mínima sobre o custo (%)"
              hint="A trava. Se o que sobrar depois das taxas não cobrir o custo mais esta margem, o anúncio não vai ao ar."
            >
              {(field) => (
                <Input
                  {...field}
                  inputMode="decimal"
                  value={minMargin}
                  onChange={(event) => {
                    setMinMargin(event.target.value)
                  }}
                />
              )}
            </Field>

            <Field
              label="Parte do frete no preço (%)"
              hint="O frete que sai “grátis” para quem compra é pago pelo vendedor. 100 = o preço cobre o frete inteiro; 50 = a loja racha; 0 = a loja absorve."
            >
              {(field) => (
                <Input
                  {...field}
                  inputMode="decimal"
                  value={shippingShare}
                  onChange={(event) => {
                    setShippingShare(event.target.value)
                  }}
                />
              )}
            </Field>

            <label className="flex items-start gap-2 text-(length:--text-sm) text-(--text-secondary)">
              <input
                type="checkbox"
                className="mt-1"
                checked={passFixedFee}
                onChange={(event) => {
                  setPassFixedFee(event.target.checked)
                }}
              />
              <span>
                Somar o custo fixo do canal ao preço
                <span className="block text-(length:--text-xs) text-(--text-muted)">
                  Desmarcado, a loja absorve. A diferença aparece inteira nos
                  itens baratos, onde o custo fixo é boa parte do valor.
                </span>
              </span>
            </label>

            <Field
              label="Arredondamento"
              hint="Sempre para cima: para baixo seria vender abaixo da conta que acabou de ser feita."
            >
              {(field) => (
                <Select
                  {...field}
                  value={rounding}
                  onChange={(event) => {
                    setRounding(event.target.value as typeof rounding)
                  }}
                >
                  {ROUNDING_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Garantia padrão (meses)"
              hint="Vale quando o produto não declarou uma. Zero no produto é “sem garantia”, e continua valendo."
            >
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={warrantyMonths}
                  onChange={(event) => {
                    setWarrantyMonths(event.target.value)
                  }}
                />
              )}
            </Field>

            <label className="flex items-start gap-2 text-(length:--text-sm) text-(--text-secondary)">
              <input
                type="checkbox"
                className="mt-1"
                checked={allowWithoutCost}
                onChange={(event) => {
                  setAllowWithoutCost(event.target.checked)
                }}
              />
              <span>
                Anunciar produto sem custo médio, com aviso
                <span className="block text-(length:--text-xs) text-(--text-muted)">
                  Sem custo não dá para conferir a margem. Marcado, o anúncio
                  vai para a revisão; desmarcado, ele fica bloqueado.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-(length:--text-sm) text-(--text-secondary)">
              <input
                type="checkbox"
                className="mt-1"
                checked={trustAiResearch}
                onChange={(event) => {
                  setTrustAiResearch(event.target.checked)
                }}
              />
              <span>
                Aceitar pesquisa da IA como informação conferida
                <span className="block text-(length:--text-xs) text-(--text-muted)">
                  Desmarcado, o que a IA pesquisar espera alguém aprovar. O que
                  ela apenas estimar espera de qualquer jeito.
                </span>
              </span>
            </label>
          </div>

          {error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={save.isPending}>
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
